// import { Timestamp } from "@firebase/firestore-types";

import { db } from "../system/firebase/index";
import { collection, doc, updateDoc } from "firebase/firestore";
import CollectionItemProfile from "./CollectionItemProfile";
import Model from "./Model";
import ItemMake from "./ItemMake";
import ItemModel from "./ItemModel";
import ItemVariant from "./ItemVariant";

// Overlaps with legacy Collection.js model (should really all just be Item)

// TODO: Eventually this should be a typescript file, so we can use types and auto-complete

/**
 * @typedef {Object} CollectionItem
 *
 * @property {string} id
 *
 * @property {string} added_by - The id of the user who added the profile
 * @property {Timestamp} date_added
 * @property {string} edited_by - The id of the user who edited the profile
 * @property {Timestamp} date_modified
 *
 * @property {Object} media // TODO: What even is this? Should it be a Media object?
 * @property {Array} notes // TODO: Does this have a consistent structure?
 */
class CollectionItem extends Model {
    static table = "collectionItems";

    static fieldAddedBy = "added_by";
    static fieldAddedDate = "date_added";
    static fieldEditedBy = "edited_by";
    static fieldEditedDate = "date_modified";

    // FIXME: maybe there should be a "fields" property that contains all the fields and their types, defaults, and validation rules
    //  - then swap constructor for a static method that creates a new object with defaults
    //  - but do I still get type hinting? probably not :(
    //  - see 005.dynamic-model-properties-with-type-hinting.txt
    static fieldMetadata = {
        year: {
            validationRules: "required",
        },
        make: {
            validationRules: "required",
        },
        model: {
            validationRules: "required",
        },
    };

    // TODO: Maybe this constructor should be private, and always create a new one from a static method?
    //  - eg. for setting added_by user, dates, item_id, etc
    constructor() {
        super();

        this.id = null;

        this.added_by = null;
        this.date_added = null; // TODO: This should be a Timestamp object
        this.edited_by = null;
        this.date_modified = null; // TODO: This should be a Timestamp object

        this.published_by = null; // TODO: What is this? Is this just a legacy field at this point?
        this.date_published = null; // TODO: As above - also, this should be a Timestamp object

        this.media = {
            "docs": [],
            "images": [],
            "videos": []
        }; // TODO: What even is this? Should it be a Media object?
        this.notes = null; // TODO: Does this have a consistent structure?

        // these are from CollectionWebProfile
        this.year = null;
        this.make = null; // id
        this.model = null; // id
        this.variant = null; // id
        this.chassisNumber = null;
        this.engineNumber = null;
        this.categories = []; // TODO: Should this be an enum? Or a Category object from another table? Or array?
        this.tags = []; // TODO: Is this needed?

        this.featuredImage = null; // TODO: This should be a Media object (filepath, filename, etc)

        // TODO: maybe this shouldn't be part of the main class? it's only used for legacy data
        this.legacyPublishedBy = null;
        this.legacyDatePublished = null;
    }

    /**
     * Delete this CollectionItem, along with all related CollectionItemProfiles
     *  - this doesn't actually care if the document existed or not (due to Firestore not giving that information)
     * 
     * @returns {boolean} - Successful?
     */
    async deleteWithProfiles() {
        if (!this.id) return false;

        const profiles = await CollectionItemProfile.where("item_id", "==", this.id);

        for (const profile of profiles) {
            // TODO: maybe fail out here if any of these are false - but then will end up with a half deleted item (eg. some profiles missing)
            const result = await profile.delete();
        };

        try {
            await this.deleteFromFirestore();
            return true;
        } catch {
            // TODO: Maybe handle this?
            return false;
        }
    }

    async getProfiles() {
        if (!this.id) return null;

        return CollectionItemProfile.where("item_id", "==", this.id);
    }

    async getProfilesOrderedByCreatedDesc() {
        let profiles = await this.getProfiles();

        profiles.sort((a, b) => {
            const aType = typeof a?.date_added?.seconds;
            const bType = typeof b?.date_added?.seconds;

            if (aType !== 'number' && bType !== 'number') return 0;
            if (aType !== 'number') return 1;
            if (bType !== 'number') return -1;

            return b?.date_added?.seconds - a?.date_added?.seconds;
        });

        return profiles;
    }

    /**
     * Create a new CollectionItemProfile object for this CollectionItem
     *
     * @param {boolean} isFirstProfile - Whether this is the first profile for this item (used to add extra defaults)
     *
     * @returns {CollectionItemProfile} - The new CollectionItemProfile object
     */
    async createNewProfile(isFirstProfile = false) {
        // FIXME: This should also handle default values (can do in the constructor - fromFirestore will still overwrite)
        const profile = new CollectionItemProfile();

        profile.item_id = this.id;
        profile.title = await this.generateProfileTitle();
        profile.slug = await profile.generateProfileSlug();

        if (isFirstProfile) {
            profile.categories = [
                CollectionItemProfile.ProfileCategory.DATABASE,
            ];
            profile.profile = "Identity";

            if (this.featuredImage) {
                profile.featuredImage = this.featuredImage;
            }
        }

        return profile;
    }

    /**
     * Generate a title for a Profile from the CollectionItem's year, make, model, variant
     *  - format: "year make model variant"
     *
     * @returns {String} - The generated title
     */
    async generateProfileTitle() {
        const [make, model, variant] = await Promise.all([
            ItemMake.find(this.make),
            ItemModel.find(this.model),
            ItemVariant.find(this.variant),
        ]);

        const titleElements = [make, model, variant]
            .filter((item) => item?.title)
            .map((item) => item.title);

        return [this.year, ...titleElements].join(" ").trim();
    }

    // TODO: map out default values for all fields (eg. null, or false, or [], or {}, etc)
    //  - is this not just handled in the constructor?

    // TODO: createNew - or handled in constructor (but able to pass in auth user, etc)
    //  - OR we just have functions for adding auth and date, bumping modified date, etc
}

export default CollectionItem;
